import { MAX_OPTION_COUNT, MIN_OPTION_COUNT } from '@cavaquinho/shared';
import { Field } from '../../ui/primitives/Field.tsx';
import { Input } from '../../ui/primitives/Input.tsx';
import { Button } from '../../ui/primitives/Button.tsx';
import { IconButton } from '../../ui/primitives/IconButton.tsx';
import { Text } from '../../ui/primitives/Text.tsx';
import { DrawerSection } from '../../ui/components/Drawer/Drawer.tsx';
import { violationsFor, type FieldViolation } from './validation.js';
import { VisibleAnswersField, fitVisibleAnswers } from './VisibleAnswersField.tsx';
import styles from './QuestionOptionsFields.module.css';

/**
 * `multiple_choice`'s question + answer-options editor (T100's pattern,
 * `note-identification/ConfigFields.tsx`), kept separate from its
 * `ConfigFields.tsx` so a future text-answered type can reuse it.
 *
 * Two named sections (§ 14 Drawer): the question, and the answers it takes.
 *
 * THE CORRECT ANSWER IS A RADIO GROUP, and it is drawn as one — a single
 * choice out of a set is what a radio IS, and a checkbox here would let an
 * administrator mark two answers correct in a form the server will reject.
 */
export interface QuestionOptionsValue {
  question: string;
  options: string[];
  correctIndex: number;
  /** How many of `options` one attempt shows; absent shows them all. */
  visibleAnswers?: number;
}

interface QuestionOptionsFieldsProps {
  value: QuestionOptionsValue;
  violations: readonly FieldViolation[];
  onChange: (next: QuestionOptionsValue) => void;
}

export function emptyQuestionOptions(): QuestionOptionsValue {
  return { question: '', options: ['', ''], correctIndex: 0 };
}

export function QuestionOptionsFields({ value, violations, onChange }: QuestionOptionsFieldsProps) {
  const questionViolations = violationsFor(violations, 'config.question');
  const optionsViolations = violationsFor(violations, 'config.options');
  const correctViolations = violationsFor(violations, 'config.correctIndex');

  function setOption(index: number, text: string) {
    const options = value.options.map((o, i) => (i === index ? text : o));
    onChange({ ...value, options });
  }

  function addOption() {
    if (value.options.length >= MAX_OPTION_COUNT) return;
    onChange({ ...value, options: [...value.options, ''] });
  }

  function removeOption(index: number) {
    if (value.options.length <= MIN_OPTION_COUNT) return;
    const options = value.options.filter((_, i) => i !== index);
    const correctIndex =
      value.correctIndex === index
        ? 0
        : value.correctIndex > index
          ? value.correctIndex - 1
          : value.correctIndex;
    onChange({
      ...value,
      options,
      correctIndex,
      visibleAnswers: fitVisibleAnswers(value.visibleAnswers, options.length),
    });
  }

  return (
    <div data-testid="question-options">
      <DrawerSection title="Pergunta" description="O enunciado que o aluno lê.">
        <Field
          size="compact"
          label="Pergunta"
          htmlFor="question"
          error={questionViolations[0]?.message}
        >
          <Input
            id="question"
            value={value.question}
            invalid={questionViolations.length > 0}
            onChange={(question) => onChange({ ...value, question })}
          />
        </Field>
      </DrawerSection>

      <DrawerSection
        title="Respostas"
        description="Marque à esquerda qual opção é a correta."
      >
        <fieldset className={styles.options} data-testid="options">
          <legend className={styles.legend}>
            <Text as="span" variant="body-sm" tone="muted">
              {value.options.length} opções · a correta é a {value.correctIndex + 1}ª
            </Text>
          </legend>

          {value.options.map((option, index) => (
            <div key={index} className={styles.option} data-testid="option-row">
              <label className={styles.correct} data-checked={value.correctIndex === index || undefined}>
                <input
                  className={styles.radio}
                  type="radio"
                  name="correctIndex"
                  checked={value.correctIndex === index}
                  onChange={() => onChange({ ...value, correctIndex: index })}
                  aria-label={`Opção ${index + 1} é a correta`}
                />
                <span className={styles.dot} aria-hidden="true" />
              </label>
              <Input
                id={`option-${index}`}
                value={option}
                placeholder={`Opção ${index + 1}`}
                onChange={(text) => setOption(index, text)}
              />
              <IconButton
                icon="trash"
                shape="square"
                size="compact"
                label={`Remover opção ${index + 1}`}
                disabled={value.options.length <= MIN_OPTION_COUNT}
                disabledReason={`Uma pergunta precisa de ao menos ${MIN_OPTION_COUNT} opções.`}
                onClick={() => removeOption(index)}
              />
            </div>
          ))}

          <span data-testid="add-option">
            {value.options.length >= MAX_OPTION_COUNT ? (
              <Button
                variant="secondary"
                size="compact"
                disabled
                disabledReason={`No máximo ${MAX_OPTION_COUNT} opções.`}
              >
                Adicionar opção
              </Button>
            ) : (
              <Button variant="secondary" size="compact" icon="plus" onClick={addOption}>
                Adicionar opção
              </Button>
            )}
          </span>

          {optionsViolations.map((violation) => (
            <Text key={violation.code} as="p" variant="body-sm" tone="error">
              {violation.message}
            </Text>
          ))}
          {correctViolations.map((violation) => (
            <Text key={violation.code} as="p" variant="body-sm" tone="error">
              {violation.message}
            </Text>
          ))}
        </fieldset>

        <VisibleAnswersField
          value={value.visibleAnswers}
          answerCount={value.options.length}
          violations={violations}
          onChange={(visibleAnswers) => onChange({ ...value, visibleAnswers })}
        />
      </DrawerSection>
    </div>
  );
}
